import { Text, removeAllToasts } from '@medly-components/core';
import { PlayCircleFilledIcon } from '@medly-components/icons';
import { useStorage } from '@medly-components/utils';
import { DialogBox } from 'components/common/DialogBox/DialogBox';
import Styled, { CoralText, PageTitle } from 'components/common/Styled';
import { SUPPORT_EMAIL_ADDRESS } from 'constants/common';
import { getMapRoute } from 'constants/routes';
import { MY_MAP } from 'constants/text/home';
import {
    HAVING_TROUBLE_WITH_MAPS,
    MAP_LEGENDS,
    MAP_TOUR_CONFIRMATION,
    MAP_TOUR_RESUME_CONFIRMATION,
    MY_MAP_CONTACT_US_DESCRIPTION,
    MY_MAP_DISCLAIMER,
    MY_MAP_DISCLAIMER_CONTENT,
    MY_MAP_MOBILE_DIALOG_CONTENT,
    MY_MAP_MOBILE_DIALOG_SUBMIT_BUTTON_LABEL,
    MY_MAP_MOBILE_DIALOG_TITLE,
    TAKE_MAP_TOUR,
    WELCOME_TO_MAPS_CONTENT
} from 'constants/text/myMap';
import { UserContext } from 'context/UserContext';
import { useBoolean, useIsMobile } from 'hooks';
import { Legends } from 'pages/Map/components/Legends/Legends';
import { useMapTour } from 'pages/Map/hooks/useMapTour/useMapTour';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import contactUsImage from '../../../../assets/images/map/orion_lifebelt.png';
import {
    Banner,
    ContactUsDescription,
    ContactUsWrapper,
    DisclaimerWrapper,
    LegendsWrapper,
    Maps,
    MapsContent,
    MapsWrapper,
    Section,
    TakeAppTourWrapper
} from './WelcomeToMaps.styled';
import { GuidedTour } from 'components/common';
import { GuidedTourStep } from 'components/common/GuidedTour/types';
import { EXIT, YOU_WANT_TO_EXIT } from 'constants/text/common';
import { MapTourContext } from 'context/MapTourContext';
import { MAP_SPECIFIC_TOUR_START_INDEX, tourSteps } from 'pages/Map/components/MapTour';
import { RESUME_TOUR, RESUME_TOUR_DESCRIPTION } from 'constants/text/mapTour';
import MapIntro from './MapIntro';
import AssignedMaps from './AssignedMaps';

export const MAP_DIALOG_COUNT_IN_MOBILE = 'mapDialogCount';

export const WelcomeToMaps: FC = () => {
    const { getKeyWithUserId } = useContext(UserContext);
    const isMobile = useIsMobile();
    const theme = useTheme();
    const [mapDialogCount, setDialogCount] = useStorage(getKeyWithUserId(MAP_DIALOG_COUNT_IN_MOBILE), { initialValue: 1 });
    const [showMobileDialog, setShowMobileDialog] = useState<boolean>(isMobile && mapDialogCount < 4);
    const { isTourFinished, setTourStepIndex } = useMapTour();
    const navigate = useNavigate();
    const { isMapTour, setIsMapTour } = useContext(MapTourContext);
    const { value: isResumeDailogOpen, setTrue: showResumeDailog, setFalse: hideResumeDialog } = useBoolean();

    useEffect(() => {
        return () => removeAllToasts();
    }, []);

    const handleMobileDialogClose = () => {
        setShowMobileDialog(false);
        setDialogCount(mapDialogCount + 1);
    };

    const navigateToMapView = useCallback(
        (region: string, zoneName: string) => {
            navigate(getMapRoute(region, zoneName));
        },
        [navigate]
    );

    const startTour = useCallback(() => {
        setIsMapTour(true);
    }, []);

    const handleGuidedTourClose = () => {
        setIsMapTour(false);
        if (isTourFinished()) {
            return;
        }
        showResumeDailog();
    };

    const handleResumeDailogExit = useCallback(() => {
        hideResumeDialog();
    }, []);

    const handleResumeDailogContinue = useCallback(() => {
        hideResumeDialog();
        setIsMapTour(true);
    }, [isTourFinished]);

    const pageWrapperRef = useRef<HTMLDivElement>(null);
    const mapZonesRef = useRef<HTMLDivElement>(null);
    const steps = tourSteps.map((step, index) => {
        if (index === 0) {
            return {
                ...step,
                targetRef: mapZonesRef,
                targetScroll: () => mapZonesRef.current?.scrollTo({ top: 0, behavior: 'smooth' }),
                onNext: () => {
                    setTourStepIndex(MAP_SPECIFIC_TOUR_START_INDEX);
                }
            };
        }
        return step;
    }) as GuidedTourStep[];

    return (
        <MapsWrapper ref={pageWrapperRef}>
            <DialogBox
                open={showMobileDialog}
                title={MY_MAP_MOBILE_DIALOG_TITLE}
                content={MY_MAP_MOBILE_DIALOG_CONTENT}
                submitButtonLabel={MY_MAP_MOBILE_DIALOG_SUBMIT_BUTTON_LABEL}
                onClose={() => setShowMobileDialog(false)}
                submit={handleMobileDialogClose}
            />
            <Banner>
                <Styled.FlexBox direction="column">
                    <PageTitle>{MY_MAP}</PageTitle>
                    <MapsContent textVariant="body1">{`${WELCOME_TO_MAPS_CONTENT}`}</MapsContent>
                </Styled.FlexBox>
                {!isMapTour && isTourFinished() ? (
                    <div>
                        <TakeAppTourWrapper onClick={startTour}>
                            <PlayCircleFilledIcon iconColor={theme.colors.coral[500]} />
                            <CoralText>{TAKE_MAP_TOUR}</CoralText>
                        </TakeAppTourWrapper>
                    </div>
                ) : null}
            </Banner>
            <Maps ref={mapZonesRef}>
                <AssignedMaps onZoneClick={navigateToMapView} />
            </Maps>
            <Section>
                <LegendsWrapper>
                    <Text textVariant="h3">{MAP_LEGENDS}</Text>
                    <Legends />
                </LegendsWrapper>
            </Section>
            <Section>
                <MapIntro />
            </Section>
            <Section>
                <ContactUsWrapper>
                    <img src={contactUsImage} alt="Contact us" />
                    <ContactUsDescription>
                        <Text textVariant="h3">{HAVING_TROUBLE_WITH_MAPS}</Text>
                        <Text>
                            {MY_MAP_CONTACT_US_DESCRIPTION}{' '}
                            <Styled.TextRouteAnchor href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>
                                {SUPPORT_EMAIL_ADDRESS}
                            </Styled.TextRouteAnchor>
                            .
                        </Text>
                    </ContactUsDescription>
                </ContactUsWrapper>
            </Section>
            <Section>
                <DisclaimerWrapper>
                    <Text textVariant="body3" textWeight="Medium" textColor={theme.colors.darkBlue}>
                        {MY_MAP_DISCLAIMER}
                    </Text>
                    <Text textVariant="body3" textColor={theme.colors.grey[700]}>
                        {MY_MAP_DISCLAIMER_CONTENT}
                    </Text>
                </DisclaimerWrapper>
            </Section>
            <GuidedTour
                showStepsCounter
                showTour={isMapTour}
                steps={steps}
                containerRef={pageWrapperRef}
                onTourStart={startTour}
                onTourClose={handleGuidedTourClose}
                tourStatusKey="map-tour-status"
                showConfirmationDialog={!showMobileDialog && !isTourFinished() && !isMapTour}
                confirmationText={MAP_TOUR_CONFIRMATION}
                resumeText={MAP_TOUR_RESUME_CONFIRMATION}
                isWithoutAction={true}
            />
            <DialogBox
                open={isResumeDailogOpen}
                title={YOU_WANT_TO_EXIT}
                content={RESUME_TOUR_DESCRIPTION}
                cancelButtonLabel={EXIT}
                submitButtonLabel={RESUME_TOUR}
                showCancel
                onClose={hideResumeDialog}
                onCancel={handleResumeDailogExit}
                submit={handleResumeDailogContinue}
            />
        </MapsWrapper>
    );
};

WelcomeToMaps.displayName = 'WelcomeToMaps';
